import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const pitchCopyText = `# NSCAT - AI-Powered Network Configuration Audit Platform
## Secure Mango LLC

### Key Metrics
- 6 Firewall Vendors: Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet
- 535+ Audits Completed
- 21 AI Models (18 Local + Paid + Open-Source LLMs)
- 8 Integrated Modules: Dashboard, Audit, Reports, SIEM, SOAR, AI Chat, LLMs, RBAC

### The Problem
Firewall misconfigurations are responsible for 99% of firewall breaches (Gartner).
- Rules are complex, vendor-specific, and constantly changing
- Manual audits take weeks and cost $150-$300/hr per consultant
- Security teams are overloaded - auditing is deprioritized
- Compliance checks are periodic snapshots, not continuous monitoring
- No cross-vendor, AI-native audit platform exists today
- Existing tools (Tufin, FireMon, AlgoSec) cost $40K-$200K/year and lack AI reasoning

### Our Solution
NSCAT automates the entire firewall audit lifecycle:
- AI-powered firewall configuration audit engine using Local, Paid & Open-Source LLMs
- Multi-vendor configuration ingestion from a live Network Lab
- Automated policy analysis correlated with NIST SP 800-53 Rev 5 Guidelines
- Risk scoring, PDF audit reports, and executive summaries
- Real-time SIEM alerting with severity-based triage
- SOAR-driven automated email dispatch to Network Teams with report links
- RAG-powered AI Chatbot for interactive audit explanation using NIST SP 800-53 knowledge base

### Product - MVP Built & Operational
Core Platform:
- Multi-vendor firewall config fetch & storage from Network Lab
- AI audit engine with Local (Ollama), Paid (OpenAI) & Open-Source LLMs (Groq, Google AI, OpenRouter)
- Audit reports correlated with NIST SP 800-53 Rev 5 Guidelines
- Extensive Dashboard with real-time audit metrics

Security Operations:
- Open-Source SIEM receiving Audit Alerts via Syslog
- Custom SOAR & SMTP modules for automated email dispatch
- RAG-powered AI Chatbot (1,014 NIST SP 800-53 controls indexed in ChromaDB)
- Role-Based Access Control (RBAC) with User Management

### How It Works
1. Fetch Configuration → Pull firewall configs from multi-vendor devices
2. Normalize & Store → Parse vendor-specific formats into unified schema
3. AI Analysis → LLM-powered policy analysis
4. NIST Correlation → Map findings to NIST SP 800-53 Rev 5 Guidelines
5. Generate Report → PDF audit report with risk scores and executive summary
6. SIEM Alert → Forward severity-based alerts to SOC via Syslog
7. SOAR Dispatch → Automated email via SOAR with report download link
8. AI Chatbot → Interactive audit explanation via RAG-powered chatbot

### Competition & Advantage
| Feature | Incumbents | NSCAT |
|---------|-----------|-------|
| AI Audit Engine | None/Limited | 21 LLMs (Local + Paid + Open-Source) |
| Multi-Vendor | Yes (proprietary) | 6 vendors from Network Lab, extensible |
| RAG AI Chatbot | None | NIST SP 800-53 RAG (1,014 controls) |
| SIEM + SOAR Pipeline | Partial/Add-on | Full end-to-end (Syslog + SOAR + SMTP) |
| Local LLM Support | None | 18 local models = zero ongoing AI cost |
| Price Point | $40K-$200K/year | Target: $8K-$25K/year |

### Business Model
- SaaS subscription: Annual license per organization
- Per-device pricing: Scale with customer's firewall estate
- MSSP licensing: Multi-tenant model for service providers
- Enterprise tier: Custom SLA, dedicated support, SSO
- Target Pricing: SMB $5K-$8K/yr | Mid-Market $8K-$15K/yr | Enterprise $15K-$25K/yr | MSSP $10K-$20K/yr

### The Ask
Seeking accelerator partnership to scale validated MVP into market-ready product:
- Enterprise & MSSP introductions for first 5-10 pilot customers
- Go-to-market mentorship for enterprise security sales strategy
- Security ecosystem partnerships (SIEM/SOAR vendor integrations)
- Product-market fit validation through structured customer discovery
- Scale engineering from research prototype to production SaaS

Status: MVP complete with 535+ audits across 6 vendors, full SIEM/SOAR pipeline, and AI chatbot operational. Ready for pilot deployments.`;

function NSCATPitch() {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSnackbar({ open: true, message: 'Pitch data refreshed' });
    } catch (err) {
      console.error('Error refreshing:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopyPitch = async () => {
    try {
      await navigator.clipboard.writeText(pitchCopyText);
      setSnackbar({ open: true, message: 'Pitch content copied to clipboard' });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = pitchCopyText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSnackbar({ open: true, message: 'Pitch content copied to clipboard' });
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadPdf = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>NSCAT - AI-Powered Network Configuration Audit Platform</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; color: #1e293b; background: white; }
            .page { page-break-after: always; padding: 50px 56px; min-height: 100vh; position: relative; }
            .page:last-child { page-break-after: auto; }
            h1 { font-size: 32px; font-weight: 700; margin-bottom: 6px; color: #1e293b; }
            h2 { font-size: 20px; font-weight: 600; margin-bottom: 14px; color: #5c6bc0; border-bottom: 2px solid #5c6bc0; padding-bottom: 6px; }
            h3 { font-size: 15px; font-weight: 500; margin-bottom: 6px; color: #475569; }
            p { font-size: 13px; line-height: 1.7; margin-bottom: 6px; color: #334155; }
            ul { list-style: none; padding: 0; }
            ul li { font-size: 13px; line-height: 1.9; padding-left: 18px; position: relative; color: #334155; }
            ul li:before { content: "\\2022"; color: #5c6bc0; font-weight: bold; position: absolute; left: 2px; }
            .section { margin-bottom: 24px; }
            .two-col { display: flex; gap: 36px; }
            .two-col > div { flex: 1; }
            .cover { display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; }
            .cover h1 { font-size: 40px; color: #5c6bc0; margin-bottom: 4px; }
            .cover .tagline { font-size: 17px; color: #475569; margin-bottom: 28px; font-weight: 400; }
            .cover .points { margin-top: 8px; }
            .cover .points li { font-size: 15px; line-height: 2; }
            .cover .company { margin-top: 36px; font-size: 14px; color: #94a3b8; font-weight: 500; }
            .stat-row { display: flex; gap: 16px; margin: 16px 0; }
            .stat-box { flex: 1; background: #f1f5f9; border-radius: 8px; padding: 12px; text-align: center; }
            .stat-box .num { font-size: 22px; font-weight: 700; color: #5c6bc0; }
            .stat-box .label { font-size: 11px; color: #64748b; margin-top: 2px; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
            th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #e2e8f0; }
            th { background: #f1f5f9; font-weight: 600; color: #475569; }
            .highlight { background: #eff6ff; border-left: 3px solid #5c6bc0; padding: 10px 14px; border-radius: 0 6px 6px 0; margin: 12px 0; font-size: 13px; }
            .footer { position: absolute; bottom: 24px; left: 56px; right: 56px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 8px; }
            @media print { .page { padding: 44px 50px; } .footer { bottom: 20px; left: 50px; right: 50px; } }
          </style>
        </head>
        <body>
          <!-- Page 1: Cover -->
          <div class="page cover">
            <h1>NSCAT</h1>
            <p class="tagline">AI-Powered Network Configuration Audit Platform</p>
            <ul class="points">
              <li>Automated AI firewall configuration auditing across 6 vendors</li>
              <li>End-to-end SOC + SIEM + SOAR integration</li>
              <li>RAG-powered AI Chatbot with NIST SP 800-53 knowledge base</li>
              <li>Built for Enterprises, SOC Teams & MSSPs</li>
            </ul>
            <p class="company">Secure Mango LLC</p>
            <div class="footer">NSCAT - Confidential</div>
          </div>

          <!-- Page 2: Problem -->
          <div class="page">
            <div class="section">
              <h2>The Problem</h2>
              <p>Firewall misconfigurations are responsible for 99% of firewall breaches (Gartner). Security teams face an impossible audit burden that grows with every new device and vendor added to their network.</p>
              <ul>
                <li>Firewall misconfigurations are the #1 cause of network breaches</li>
                <li>Rules are complex, vendor-specific, and constantly changing</li>
                <li>Manual audits take weeks and cost $150-$300/hr per consultant</li>
                <li>Security teams are overloaded - auditing is deprioritized</li>
                <li>Compliance checks are periodic snapshots, not continuous monitoring</li>
                <li>No cross-vendor, AI-native audit platform exists today</li>
                <li>Existing tools (Tufin, FireMon, AlgoSec) cost $40K-$200K/year and lack AI reasoning</li>
              </ul>
            </div>
            <div class="section">
              <h2>Our Solution</h2>
              <p>NSCAT automates the entire firewall audit lifecycle - from configuration ingestion to AI-driven policy analysis, compliance reporting, and automated SOC alerting.</p>
              <ul>
                <li>AI-powered firewall configuration audit engine using Local, Paid & Open-Source LLMs</li>
                <li>Multi-vendor configuration ingestion from a live Network Lab</li>
                <li>Automated policy analysis correlated with NIST SP 800-53 Rev 5 Guidelines</li>
                <li>Risk scoring, PDF audit reports, and executive summaries</li>
                <li>Real-time SIEM alerting with severity-based triage</li>
                <li>SOAR-driven automated email dispatch to Network Teams with report links</li>
                <li>RAG-powered AI Chatbot for interactive audit explanation using NIST SP 800-53 knowledge base</li>
              </ul>
            </div>
            <div class="footer">NSCAT - Confidential</div>
          </div>

          <!-- Page 3: Product - MVP Status -->
          <div class="page">
            <div class="section">
              <h2>Product - MVP Built & Operational</h2>
              <p>Fully functional research prototype integrated end-to-end across all modules. Not a mockup - a working system auditing real firewall configurations.</p>
              <div class="stat-row">
                <div class="stat-box"><div class="num">6</div><div class="label">Firewall Vendors</div></div>
                <div class="stat-box"><div class="num">535+</div><div class="label">Audits Completed</div></div>
                <div class="stat-box"><div class="num">21</div><div class="label">AI Models</div></div>
                <div class="stat-box"><div class="num">8</div><div class="label">Integrated Modules</div></div>
              </div>
            </div>
            <div class="two-col">
              <div class="section">
                <h3>Core Platform</h3>
                <ul>
                  <li>Multi-vendor firewall config fetch & storage (Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet)</li>
                  <li>AI audit engine with Local (Ollama), Paid (OpenAI) & Open-Source LLMs (Groq, Google AI, OpenRouter)</li>
                  <li>Audit reports correlated with NIST SP 800-53 Rev 5 Security and Privacy Controls for Information Systems and Organizations</li>
                  <li>Extensive Dashboard with real-time metrics</li>
                  <li>Historical Audit Reports module for longitudinal tracking</li>
                </ul>
              </div>
              <div class="section">
                <h3>Security Operations</h3>
                <ul>
                  <li>Open-Source SIEM integration receiving Audit Alerts via Syslog</li>
                  <li>Custom SOAR module with automated email dispatch</li>
                  <li>Configurable response time & email templates</li>
                  <li>Audit Report download links in alert emails</li>
                  <li>RAG-powered AI Chatbot (1,014 NIST SP 800-53 controls indexed in ChromaDB)</li>
                  <li>Role-Based Access Control (RBAC) with User Management</li>
                </ul>
              </div>
            </div>
            <div class="footer">NSCAT - Confidential</div>
          </div>

          <!-- Page 4: How It Works -->
          <div class="page">
            <div class="section">
              <h2>How It Works</h2>
              <p>End-to-end automated pipeline from firewall configuration to SOC alerting, designed for zero manual intervention after initial setup.</p>
              <table>
                <tr><th style="width:30px">#</th><th>Step</th><th>Detail</th></tr>
                <tr><td>1</td><td>Fetch Configuration</td><td>Pull firewall configs from multi-vendor devices in Network Lab (Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet)</td></tr>
                <tr><td>2</td><td>Normalize & Store</td><td>Parse vendor-specific formats into unified schema, store configurations</td></tr>
                <tr><td>3</td><td>AI Analysis</td><td>LLM-powered policy analysis using Local, Paid or Open-Source models</td></tr>
                <tr><td>4</td><td>NIST Correlation</td><td>Map audit findings to NIST SP 800-53 Rev 5 Security and Privacy Controls for Information Systems and Organizations</td></tr>
                <tr><td>5</td><td>Generate Report</td><td>Produce detailed PDF audit report with risk scores and executive summary</td></tr>
                <tr><td>6</td><td>SIEM Alert</td><td>Forward severity-based alerts (Critical/High/Medium) to SOC via Syslog</td></tr>
                <tr><td>7</td><td>SOAR Dispatch</td><td>Automated email to Network Team via custom SOAR & SMTP modules with report download link</td></tr>
                <tr><td>8</td><td>AI Chatbot</td><td>Interactive audit explanation and compliance Q&A via RAG-powered chatbot</td></tr>
              </table>
            </div>
            <div class="section">
              <h2>Why Now</h2>
              <ul>
                <li>Firewall estates are growing more complex - multi-vendor, multi-cloud, hybrid</li>
                <li>LLM reasoning on network configurations is now production-viable</li>
                <li>Regulatory compliance pressure accelerating (NIS2, DORA, PCI-DSS 4.0)</li>
                <li>SOC automation demand surging - 70% of SOCs plan to increase automation (SANS 2024)</li>
                <li>Global shortage of 3.4M cybersecurity professionals (ISC2 2024)</li>
                <li>Shift from periodic to continuous security audit models</li>
              </ul>
            </div>
            <div class="footer">NSCAT - Confidential</div>
          </div>

          <!-- Page 5: Market & Competition -->
          <div class="page">
            <div class="two-col">
              <div class="section">
                <h2>Market</h2>
                <p><strong>TAM:</strong> $1.5B - $2B (Firewall Management & Compliance)</p>
                <ul>
                  <li>Enterprises with complex multi-vendor firewall estates</li>
                  <li>Managed Security Service Providers (MSSPs)</li>
                  <li>SOC/Security Operations teams</li>
                  <li>Regulated industries (Finance, Healthcare, Government, Energy)</li>
                </ul>
                <h3 style="margin-top: 14px;">Buyers</h3>
                <ul>
                  <li>CISOs and VP Security</li>
                  <li>SOC Directors and Managers</li>
                  <li>Network Security Engineers</li>
                  <li>Compliance Officers</li>
                </ul>
              </div>
              <div class="section">
                <h2>Competition</h2>
                <table>
                  <tr><th>Feature</th><th>Incumbents</th><th>NSCAT</th></tr>
                  <tr><td>AI Audit Engine</td><td>None / Limited</td><td>21 LLMs (Local + Paid + Open)</td></tr>
                  <tr><td>Multi-Vendor</td><td>Yes</td><td>6 vendors, extensible</td></tr>
                  <tr><td>RAG Chatbot</td><td>None</td><td>NIST SP 800-53 RAG (1,014 controls)</td></tr>
                  <tr><td>SIEM + SOAR</td><td>Partial</td><td>Full end-to-end pipeline</td></tr>
                  <tr><td>Price</td><td>$40K-$200K/yr</td><td>Target: $8K-$25K/yr</td></tr>
                  <tr><td>AI Cost</td><td>Proprietary lock-in</td><td>Local LLMs = zero AI cost</td></tr>
                </table>
                <div class="highlight">
                  <strong>Key Differentiator:</strong> Only platform combining multi-vendor AI-native auditing, RAG-powered compliance chatbot, and full SOC/SIEM/SOAR pipeline at 5-10x lower price point than incumbents.
                </div>
              </div>
            </div>
            <div class="footer">NSCAT - Confidential</div>
          </div>

          <!-- Page 6: Business Model + The Ask -->
          <div class="page">
            <div class="two-col">
              <div class="section">
                <h2>Business Model</h2>
                <ul>
                  <li><strong>SaaS subscription</strong> - Annual license per organization</li>
                  <li><strong>Per-device pricing</strong> - Scale with customer's firewall estate</li>
                  <li><strong>MSSP licensing</strong> - Multi-tenant model for service providers</li>
                  <li><strong>Enterprise tier</strong> - Custom SLA, dedicated support, SSO</li>
                  <li><strong>Add-ons</strong> - Advanced compliance frameworks (PCI-DSS, HIPAA), custom AI models</li>
                </ul>
                <h3 style="margin-top: 14px;">Target Pricing</h3>
                <ul>
                  <li>SMB: $5K - $8K/year</li>
                  <li>Mid-Market: $8K - $15K/year</li>
                  <li>Enterprise: $15K - $25K/year</li>
                  <li>MSSP: $10K - $20K/year + per-client fees</li>
                </ul>
              </div>
              <div class="section">
                <h2>The Ask</h2>
                <p>We are seeking accelerator partnership to scale our validated MVP into a market-ready product.</p>
                <ul>
                  <li><strong>Enterprise & MSSP introductions</strong> - Access to first 5-10 pilot customers</li>
                  <li><strong>Go-to-market mentorship</strong> - Enterprise security sales strategy</li>
                  <li><strong>Security ecosystem partnerships</strong> - SIEM/SOAR vendor integrations</li>
                  <li><strong>Product-market fit validation</strong> - Structured customer discovery</li>
                  <li><strong>Scale engineering</strong> - Move from research prototype to production SaaS</li>
                </ul>
                <div class="highlight">
                  <strong>Current Status:</strong> MVP complete with 535+ audits across 6 vendors, full SIEM/SOAR pipeline, and AI chatbot operational. Ready for pilot deployments.
                </div>
              </div>
            </div>
            <div class="footer">NSCAT - Confidential | Secure Mango LLC</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const BulletItem = ({ text, bold }) => (
    <ListItem sx={{ py: 0.25, px: 0 }}>
      <ListItemIcon sx={{ minWidth: 24 }}>
        <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{ variant: 'body2', fontWeight: bold ? 600 : 400 }}
      />
    </ListItem>
  );

  const CheckItem = ({ text, color }) => (
    <ListItem sx={{ py: 0.25, px: 0 }}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        <CheckCircleIcon sx={{ fontSize: 16 }} color={color || 'success'} />
      </ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ variant: 'body2' }} />
    </ListItem>
  );

  const SectionCard = ({ icon, title, color, children }) => (
    <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: color || 'primary.main' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/mvp-overview')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 500 }}
      >
        Back to MVP Overview
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1e293b', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <RocketLaunchIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={600}>
                NSCAT
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: 500 }}>
                AI-Powered Network Configuration Audit Platform
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                Automated AI firewall auditing | Multi-vendor + SOC + SIEM + SOAR | Built for Enterprises & MSSPs
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                Secure Mango LLC
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Refresh Pitch Data">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  '&:disabled': { color: 'rgba(255, 255, 255, 0.5)' }
                }}
              >
                {refreshing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Pitch Content">
              <IconButton
                onClick={handleCopyPitch}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Pitch Deck as PDF">
              <IconButton
                onClick={handleDownloadPdf}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Key Metrics Bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Firewall Vendors', value: '6', sub: 'Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet' },
          { label: 'Audits Completed', value: '535+', sub: 'AI-powered audit reports generated' },
          { label: 'AI Models', value: '21', sub: '18 Local + Paid + Open-Source LLMs' },
          { label: 'Integrated Modules', value: '8', sub: 'Dashboard, Audit, Reports, SIEM, SOAR, AI Chat, LLMs, RBAC' },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#0f172a' }}>
              <Typography variant="h4" color="primary" fontWeight={700}>{stat.value}</Typography>
              <Typography variant="subtitle2" fontWeight={600}>{stat.label}</Typography>
              <Typography variant="caption" color="text.secondary">{stat.sub}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Problem */}
      <SectionCard icon={<WarningAmberIcon color="error" />} title="The Problem" color="error.main">
        <Typography variant="body2" color="text.secondary" paragraph>
          Firewall misconfigurations are responsible for 99% of firewall breaches (Gartner). Security teams face an impossible audit burden that grows with every new device and vendor.
        </Typography>
        <List dense disablePadding>
          <BulletItem text="Firewall misconfigurations are the #1 cause of network breaches" />
          <BulletItem text="Rules are complex, vendor-specific, and constantly changing" />
          <BulletItem text="Manual audits take weeks and cost $150-$300/hr per consultant" />
          <BulletItem text="Security teams are overloaded - auditing is deprioritized" />
          <BulletItem text="Compliance checks are periodic snapshots, not continuous monitoring" />
          <BulletItem text="No cross-vendor, AI-native audit platform exists today" />
          <BulletItem text="Existing tools (Tufin, FireMon, AlgoSec) cost $40K-$200K/year and lack AI reasoning" />
        </List>
      </SectionCard>

      {/* Solution */}
      <SectionCard icon={<LightbulbIcon color="success" />} title="Our Solution" color="success.main">
        <Typography variant="body2" color="text.secondary" paragraph>
          NSCAT automates the entire firewall audit lifecycle - from configuration ingestion to AI-driven policy analysis, compliance reporting, and automated SOC alerting.
        </Typography>
        <List dense disablePadding>
          <BulletItem text="AI-powered firewall configuration audit engine using Local, Paid & Open-Source LLMs" />
          <BulletItem text="Multi-vendor configuration ingestion from a live Network Lab" />
          <BulletItem text="Automated policy analysis correlated with NIST SP 800-53 Rev 5 Security and Privacy Controls for Information Systems and Organizations" />
          <BulletItem text="Risk scoring, PDF audit reports, and executive summaries" />
          <BulletItem text="Real-time SIEM alerting with severity-based triage (Critical/High/Medium)" />
          <BulletItem text="SOAR-driven automated email dispatch to Network Teams with report download links" />
          <BulletItem text="RAG-powered AI Chatbot for interactive audit explanation using NIST SP 800-53 knowledge base" />
        </List>
      </SectionCard>

      {/* Product - MVP Status (Full Width) */}
      <SectionCard icon={<BuildIcon color="primary" />} title="Product - MVP Built & Operational" color="primary.main">
        <Typography variant="body2" color="text.secondary" paragraph>
          Fully functional research prototype integrated end-to-end across all modules. Not a mockup - a working system auditing real firewall configurations in a Network Lab environment.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
              Core Platform
            </Typography>
            <List dense disablePadding>
              <CheckItem text="Multi-vendor firewall config fetch & storage (Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet) from Network Lab" />
              <CheckItem text="AI audit engine with Local (Ollama), Paid (OpenAI) & Open-Source LLMs (Groq, Google AI, OpenRouter)" />
              <CheckItem text="Audit reports correlated with NIST SP 800-53 Rev 5 Security and Privacy Controls for Information Systems and Organizations" />
              <CheckItem text="Extensive Dashboard with real-time audit metrics and compliance overview" />
              <CheckItem text="Historical Audit Reports module for longitudinal compliance tracking" />
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
              Security Operations & Integrations
            </Typography>
            <List dense disablePadding>
              <CheckItem text="Open-Source SIEM receiving Audit Alerts from Syslog Module" />
              <CheckItem text="Custom SOAR & SMTP modules for automated email dispatch with configurable response time" />
              <CheckItem text="Audit Report download link included in alert emails to Network Team" />
              <CheckItem text="RAG-powered AI Chatbot (1,014 NIST SP 800-53 controls indexed in ChromaDB)" />
              <CheckItem text="Role-Based Access Control (RBAC) with custom User Management Module" />
              <CheckItem text="Professional UI/UX across Dashboard, Auditing, Reporting, SIEM, SOAR, AI Chat, LLMs & User Management" />
            </List>
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>MVP Validation:</strong> 535+ AI-powered audits completed successfully across 6 firewall vendors. All audit alerts forwarded to SIEM. Automated SOAR emails dispatched to Network Teams with report links.
          </Typography>
        </Alert>
      </SectionCard>

      {/* How It Works */}
      <SectionCard icon={<SettingsIcon color="info" />} title="How It Works" color="info.main">
        <Typography variant="body2" color="text.secondary" paragraph>
          End-to-end automated pipeline from firewall configuration to SOC alerting, designed for zero manual intervention after initial setup.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}><strong>#</strong></TableCell>
                <TableCell><strong>Step</strong></TableCell>
                <TableCell><strong>Detail</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ['1', 'Fetch Configuration', 'Pull firewall configs from multi-vendor devices in Network Lab (Cisco, PaloAlto, Huawei, Juniper, Checkpoint, Fortinet)'],
                ['2', 'Normalize & Store', 'Parse vendor-specific formats into unified schema, store configurations securely'],
                ['3', 'AI Analysis', 'LLM-powered policy analysis using Local, Paid or Open-Source models'],
                ['4', 'NIST Correlation', 'Map audit findings to NIST SP 800-53 Rev 5 Security and Privacy Controls for Information Systems and Organizations'],
                ['5', 'Generate Report', 'Produce detailed PDF audit report with risk scores and executive summary'],
                ['6', 'SIEM Alert', 'Forward severity-based alerts (Critical/High/Medium) to SOC via Syslog Module'],
                ['7', 'SOAR Dispatch', 'Automated email to Network Team via custom SOAR & SMTP modules with report download link'],
                ['8', 'AI Chatbot', 'Interactive audit explanation and compliance Q&A via RAG-powered chatbot'],
              ].map(([num, step, detail]) => (
                <TableRow key={num}>
                  <TableCell><Chip label={num} size="small" color="primary" sx={{ fontWeight: 600, minWidth: 28 }} /></TableCell>
                  <TableCell><strong>{step}</strong></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{detail}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>

      {/* Why Now + Market - Side by Side */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard icon={<AccessTimeIcon color="warning" />} title="Why Now" color="warning.main">
            <List dense disablePadding>
              <BulletItem text="Firewall estates growing more complex - multi-vendor, multi-cloud, hybrid" />
              <BulletItem text="LLM reasoning on network configurations is now production-viable" />
              <BulletItem text="Regulatory compliance pressure accelerating (NIS2, DORA, PCI-DSS 4.0)" />
              <BulletItem text="70% of SOCs plan to increase automation (SANS 2024)" />
              <BulletItem text="Global shortage of 3.4M cybersecurity professionals (ISC2 2024)" />
              <BulletItem text="Shift from periodic to continuous security audit models" />
            </List>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard icon={<PeopleIcon color="secondary" />} title="Market & Customers" color="secondary.main">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>TAM: $1.5B - $2B</strong> (Firewall Management & Compliance)
            </Typography>
            <List dense disablePadding>
              <BulletItem text="Enterprises with complex multi-vendor firewall estates" />
              <BulletItem text="Managed Security Service Providers (MSSPs)" />
              <BulletItem text="SOC / Security Operations teams" />
              <BulletItem text="Regulated industries (Finance, Healthcare, Government, Energy)" />
            </List>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
              Buyers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['CISOs', 'SOC Directors', 'Network Security Engineers', 'Compliance Officers', 'VP Security'].map((b) => (
                <Chip key={b} label={b} size="small" variant="outlined" />
              ))}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Competition & Advantage */}
      <SectionCard icon={<EmojiEventsIcon sx={{ color: '#f59e0b' }} />} title="Competition & Advantage" color="warning.main">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell><strong>Feature</strong></TableCell>
                <TableCell align="center"><strong>Incumbents</strong></TableCell>
                <TableCell align="center"><strong>NSCAT</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ['AI Audit Engine', 'None / Limited', '21 LLMs (Local + Paid + Open-Source)'],
                ['Multi-Vendor Support', 'Yes (proprietary)', '6 vendors from Network Lab, extensible'],
                ['RAG AI Chatbot', 'None', 'NIST SP 800-53 RAG (1,014 controls)'],
                ['SIEM + SOAR Pipeline', 'Partial / Add-on', 'Full end-to-end (Syslog + SOAR + SMTP)'],
                ['Local LLM Support', 'None', '18 local models = zero ongoing AI cost'],
                ['Price Point', '$40K-$200K/year', 'Target: $8K-$25K/year'],
              ].map(([feature, incumbents, nscat]) => (
                <TableRow key={feature}>
                  <TableCell><strong>{feature}</strong></TableCell>
                  <TableCell align="center"><Typography variant="body2" color="text.secondary">{incumbents}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2" color="success.main" fontWeight={500}>{nscat}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Key Differentiator:</strong> Only platform combining multi-vendor AI-native auditing, RAG-powered compliance chatbot, and full SOC/SIEM/SOAR pipeline at 5-10x lower price point than incumbents.
          </Typography>
        </Alert>
      </SectionCard>

      {/* Business Model + The Ask - Side by Side */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard icon={<AttachMoneyIcon color="success" />} title="Business Model" color="success.main">
            <List dense disablePadding>
              <BulletItem text="SaaS subscription - Annual license per organization" bold />
              <BulletItem text="Per-device pricing - Scale with customer's firewall estate" bold />
              <BulletItem text="MSSP licensing - Multi-tenant model for service providers" bold />
              <BulletItem text="Enterprise tier - Custom SLA, dedicated support, SSO" bold />
              <BulletItem text="Add-ons - Advanced compliance frameworks, custom AI models" bold />
            </List>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
              Target Pricing
            </Typography>
            <Grid container spacing={1}>
              {[
                { tier: 'SMB', price: '$5K-$8K/yr' },
                { tier: 'Mid-Market', price: '$8K-$15K/yr' },
                { tier: 'Enterprise', price: '$15K-$25K/yr' },
                { tier: 'MSSP', price: '$10K-$20K/yr' },
              ].map((p) => (
                <Grid item xs={6} key={p.tier}>
                  <Paper sx={{ p: 1, textAlign: 'center', bgcolor: '#0f172a' }}>
                    <Typography variant="caption" color="text.secondary">{p.tier}</Typography>
                    <Typography variant="subtitle2" color="success.main" fontWeight={600}>{p.price}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard icon={<HandshakeIcon color="primary" />} title="The Ask" color="primary.main">
            <Typography variant="body2" color="text.secondary" paragraph>
              Seeking accelerator partnership to scale our validated MVP into a market-ready product.
            </Typography>
            <List dense disablePadding>
              <CheckItem text="Enterprise & MSSP introductions - Access to first 5-10 pilot customers" color="primary" />
              <CheckItem text="Go-to-market mentorship - Enterprise security sales strategy" color="primary" />
              <CheckItem text="Security ecosystem partnerships - SIEM/SOAR vendor integrations" color="primary" />
              <CheckItem text="Product-market fit validation - Structured customer discovery" color="primary" />
              <CheckItem text="Scale engineering - Research prototype to production SaaS" color="primary" />
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Status:</strong> MVP complete with 535+ audits across 6 vendors, full SIEM/SOAR pipeline, and AI chatbot operational. Ready for pilot deployments.
              </Typography>
            </Alert>
          </SectionCard>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default NSCATPitch;
